import React from "react";
import styled from "@emotion/styled";

const Container = styled.div`
	max-width: 900px;
	margin: 40px auto;
	padding: 32px;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
	line-height: 1.7;
	color: #333;

	h1 {
		font-size: 28px;
		margin-bottom: 8px;
	}

	h2 {
		font-size: 20px;
		margin-top: 28px;
		margin-bottom: 8px;
	}

	p,
	ul {
		margin-bottom: 12px;
	}

	ul {
		padding-left: 24px;
	}

	.effective-date {
		color: #888;
		font-size: 14px;
		margin-bottom: 24px;
	}
`;

const PrivacyPolicy: React.FC = () => {
	return (
		<Container>
			<h1>Privacy Policy</h1>
			<p className="effective-date">Effective Date: February 25, 2026</p>

			<h2>1. Introduction</h2>
			<p>
				This Privacy Policy describes how Marathon Ventures Media Sales
				(&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) collects, uses, and protects information
				through the Nosey Frontend application (the &ldquo;Platform&rdquo;). This Platform
				is an internal tool intended exclusively for authorized employees of
				Marathon Ventures Media Sales.
			</p>

			<h2>2. Information We Collect</h2>
			<p>
				When you use the Platform, we may collect the following types of
				information:
			</p>
			<ul>
				<li>
					<strong>Authentication Data:</strong> Information provided through
					Google Single Sign-On (SSO), including your name, email address,
					profile picture, and email verification status.
				</li>
				<li>
					<strong>Usage Data:</strong> Information about how you interact with
					the Platform, including pages visited, features used, actions
					performed, and timestamps of activity.
				</li>
				<li>
					<strong>User Preferences:</strong> Settings and preferences you
					configure within the Platform, such as display options, page sizes,
					and column visibility.
				</li>
				<li>
					<strong>Session Data:</strong> Browser session information necessary
					to maintain your authenticated state while using the Platform.
				</li>
			</ul>

			<h2>3. How We Use Your Information</h2>
			<p>We use the information collected for the following purposes:</p>
			<ul>
				<li>
					<strong>Authentication and Access Control:</strong> To verify your
					identity and ensure only authorized employees can access the
					Platform.
				</li>
				<li>
					<strong>Platform Operation:</strong> To provide and maintain the
					Platform&apos;s functionality, including personalized settings and
					preferences.
				</li>
				<li>
					<strong>Security and Auditing:</strong> To monitor for unauthorized
					access, maintain security logs, and comply with internal audit
					requirements.
				</li>
				<li>
					<strong>Improvement:</strong> To understand usage patterns and
					improve the Platform&apos;s features and performance.
				</li>
			</ul>

			<h2>4. Data Storage and Security</h2>
			<p>
				Your information is stored on secure servers managed by Marathon
				Ventures Media Sales and its authorized service providers. We implement
				appropriate technical and organizational measures to protect your
				information, including:
			</p>
			<ul>
				<li>Encrypted data transmission (HTTPS/TLS).</li>
				<li>Secure authentication via Google SSO.</li>
				<li>Access controls limiting data visibility to authorized personnel.</li>
				<li>Regular security assessments and monitoring.</li>
			</ul>

			<h2>5. Data Sharing</h2>
			<p>
				We do not sell or share your personal information with third parties
				outside of Marathon Ventures Media Sales, except:
			</p>
			<ul>
				<li>
					With authorized service providers who assist in operating the
					Platform (e.g., cloud hosting, authentication services).
				</li>
				<li>When required by law or to comply with legal processes.</li>
				<li>
					To protect the rights, property, or safety of Marathon Ventures Media
					Sales, its employees, or others.
				</li>
			</ul>

			<h2>6. Data Retention</h2>
			<p>
				We retain your information for as long as you are an active employee
				with access to the Platform. Upon termination of employment or access,
				your personal data will be retained in accordance with Marathon Ventures
				Media Sales&apos; data retention policies and applicable legal requirements.
			</p>

			<h2>7. Your Rights</h2>
			<p>As an employee, you have the right to:</p>
			<ul>
				<li>Access the personal information we hold about you.</li>
				<li>
					Request correction of inaccurate information in your profile.
				</li>
				<li>
					Request information about how your data is used within the Platform.
				</li>
				<li>
					Raise concerns about data handling practices with the IT department
					or management.
				</li>
			</ul>

			<h2>8. Cookies and Session Storage</h2>
			<p>
				The Platform uses browser session storage and cookies to maintain your
				authentication state and user preferences. These are essential for the
				Platform to function correctly and are not used for tracking or
				advertising purposes.
			</p>

			<h2>9. Changes to This Policy</h2>
			<p>
				Marathon Ventures Media Sales reserves the right to update this Privacy
				Policy at any time. Changes will be posted on the Platform, and your
				continued use of the Platform constitutes acceptance of the updated
				policy.
			</p>

			<h2>10. Contact</h2>
			<p>
				If you have questions or concerns about this Privacy Policy or the
				handling of your data, please contact your manager or the IT department
				at Marathon Ventures Media Sales.
			</p>
		</Container>
	);
};

export default PrivacyPolicy;
